/**
 * Admin Authentication Utilities
 * 
 * Handles admin-specific authentication with separate token storage.
 * Uses 'adminAuthToken' cookie to distinguish from regular user token.
 * 
 * Security Features:
 * - JWT token validation with server (/api/admin/validate)
 * - Automatic logout on 401 responses
 * - Token included in Authorization header for protected endpoints
 * - Only logout on authentication errors (401/403), not on transient errors
 * - Uses secure cookies with js-cookie library
 */

import Cookies from 'js-cookie';

interface AdminLoginResponse {
  message: string;
  token: string;
  admin: {
    id: string;
    email: string;
    name: string;
  };
}

interface AdminValidateResponse {
  valid: boolean;
  admin: {
    id: string;
    email: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
}

interface AdminUser {
  id: string;
  email: string;
  name: string;
}

// Cookie configuration - secure in production
const isProduction = window.location.protocol === 'https:';

export const loginAdmin = async (email: string, password: string): Promise<AdminLoginResponse> => {
  const response = await fetch('/api/admin/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erro ao fazer login de admin');
  }

  const data: AdminLoginResponse = await response.json();
  
  // Store token and admin data in cookies
  setAdminAuthToken(data.token);
  setAdminData(data.admin);
  
  return data;
};

export const setAdminAuthToken = (token: string) => {
  Cookies.set('adminAuthToken', token, {
    expires: 7, // 7 days
    path: '/',
    secure: isProduction,
    sameSite: 'Lax'
  });
};

export const getAdminAuthToken = (): string | null => {
  return Cookies.get('adminAuthToken') || null;
};

export const removeAdminAuthToken = () => {
  Cookies.remove('adminAuthToken', { path: '/' });
};

export const setAdminData = (admin: AdminUser) => {
  Cookies.set('adminData', JSON.stringify(admin), {
    expires: 7,
    path: '/',
    secure: isProduction,
    sameSite: 'Lax'
  });
};

export const getAdminData = (): AdminUser | null => {
  const data = Cookies.get('adminData');
  if (!data) return null;
  
  try {
    return JSON.parse(data);
  } catch (error) {
    console.error('Failed to parse admin data:', error);
    return null;
  }
};

export const removeAdminData = () => {
  Cookies.remove('adminData', { path: '/' });
};

export const logoutAdmin = () => {
  removeAdminAuthToken();
  removeAdminData();
};

export const validateAdminToken = async (): Promise<boolean> => {
  const token = getAdminAuthToken();
  
  if (!token) {
    return false;
  }

  try {
    const response = await fetch('/api/admin/validate', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Only logout on authentication errors (401/403)
    if (response.status === 401 || response.status === 403) {
      logoutAdmin();
      return false;
    }

    if (!response.ok) {
      // Server error or other issue - keep token but return false
      console.warn('Admin token validation returned non-OK status:', response.status);
      return false;
    }

    const data: AdminValidateResponse = await response.json();
    
    if (!data.valid) {
      // Server says token is not valid
      logoutAdmin();
      return false;
    }

    // Token is valid, update admin data with latest info from server
    const adminData: AdminUser = {
      id: data.admin.id,
      email: data.admin.email,
      name: data.admin.name,
    };
    setAdminData(adminData);

    return true;
  } catch (error) {
    // Network error or server down, keep token but return false
    console.error('Admin token validation failed:', error);
    return false;
  }
};;

export const fetchWithAdminAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getAdminAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // If we get a 401, token is invalid - logout user
  if (response.status === 401) {
    logoutAdmin();
    window.location.href = '/admin/login';
  }

  return response;
};
